// src/components/FinancialStatementsManagement.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, doc, setDoc } from 'firebase/firestore';
import { BarChart2, ClipboardList, BookOpen, DollarSign, RefreshCw } from 'lucide-react'; 

// Fonction utilitaire de formatage
const formatAmount = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(amount);
};

// ID du document pour les capitaux permanents
const METADATA_DOC_ID = "capital_reserves";

const FinancialStatementsManagement = ({ db, schoolId, userRole, setMessage, setError }) => {
    
    // --- Permissions ---
    const canAccessFinancials = ['superAdmin', 'admin', 'financier'].includes(userRole);
    const canManageCapitals = ['superAdmin', 'admin'].includes(userRole);

    const [activeTab, setActiveTab] = useState('balanceSheet');
    const [isLoading, setIsLoading] = useState(true);

    // --- États pour les Données Financières (Capitaux) ---
    const [financialMetadata, setFinancialMetadata] = useState({ capitalSocial: 0, reserves: 0 });
    const [tempMetadata, setTempMetadata] = useState({ capitalSocial: 0, reserves: 0 });

    // --- États pour les Données Brutes (Récupérées des autres modules) ---
    const [revenuesData, setRevenuesData] = useState([]);          // Source: PaiementsManagement
    const [expensesData, setExpensesData] = useState([]);          // Source: DepensesManagement
    const [bankAccountsData, setBankAccountsData] = useState([]);  // Source: BankingManagement
    const [materialsData, setMaterialsData] = useState([]);        // Source: MaterielMobilierManagement
    const [consumablesData, setConsumablesData] = useState([]);    // Source: ConsumablesManagement
    const [creancesClientsData, setCreancesClientsData] = useState([]); // Source: PaiementsManagement (Dettes élèves)
    const [dettesFournisseursData, setDettesFournisseursData] = useState([]); // Source: DepensesManagement
    const [dettesLongTermeData, setDettesLongTermeData] = useState([]);     // Source: DepensesManagement

    const [balanceSheetData, setBalanceSheetData] = useState(null);
    const [incomeStatementData, setIncomeStatementData] = useState(null);

    // ====================================================================================
    // 1. CHARGEMENT DES DONNÉES (UseEffect Centralisé)
    // ====================================================================================
    useEffect(() => {
        if (!db || !schoolId || !canAccessFinancials) return;
        setIsLoading(true);

        // A. Méta-données (Capital & Réserves)
        const metadataDocRef = doc(db, 'schools', schoolId, 'financial_metadata', METADATA_DOC_ID);
        const unsubMetadata = onSnapshot(metadataDocRef, (docSnap) => {
            const data = docSnap.exists() ? docSnap.data() : { capitalSocial: 0, reserves: 0 };
            setFinancialMetadata(data);
            setTempMetadata(data);
        });

        // B. Helpers pour les sous-collections de l'école
        const subColl = (name) => collection(db, 'schools', schoolId, name);
        
        // C. Listeners
        const unsubPaiements = onSnapshot(subColl('paiements'), (snap) => setRevenuesData(snap.docs.map(d => d.data())));
        const unsubDepenses = onSnapshot(subColl('depenses'), (snap) => setExpensesData(snap.docs.map(d => d.data())));
        const unsubMaterials = onSnapshot(subColl('materials'), (snap) => setMaterialsData(snap.docs.map(d => d.data())));
        const unsubCreances = onSnapshot(subColl('creances_clients'), (snap) => setCreancesClientsData(snap.docs.map(d => d.data())));
        const unsubDettesF = onSnapshot(subColl('dettes_fournisseurs'), (snap) => setDettesFournisseursData(snap.docs.map(d => d.data())));
        const unsubEmprunts = onSnapshot(subColl('emprunts_lt'), (snap) => setDettesLongTermeData(snap.docs.map(d => d.data())));

        // D. Listener Spécial : Comptes Bancaires (Collection racine filtrée par schoolId)
        const qBank = query(collection(db, 'bankAccounts'), where('schoolId', '==', schoolId));
        const unsubBank = onSnapshot(qBank, (snap) => setBankAccountsData(snap.docs.map(d => d.data())));

        // E. Listener Spécial : Consommables (Structure différente selon ConsumablesManagement.txt)
        // Note: ConsumablesManagement utilise `artifacts/${appId}/public/data/consumables`
        // Nous supposons ici que schoolId correspond à appId, sinon il faudra ajuster le chemin.
        const consumablesPath = `artifacts/${schoolId}/public/data/consumables`;
        const unsubConsumables = onSnapshot(collection(db, consumablesPath), (snap) => {
            setConsumablesData(snap.docs.map(d => d.data()));
        }, (err) => {
            console.warn("Consumables path not found (might differ from schoolId):", err);
            setConsumablesData([]); // Fallback vide
        });

        setIsLoading(false);

        return () => {
            unsubMetadata(); unsubPaiements(); unsubDepenses(); unsubMaterials();
            unsubCreances(); unsubDettesF(); unsubEmprunts(); unsubBank(); unsubConsumables();
        };
    }, [db, schoolId, canAccessFinancials]);


    // ====================================================================================
    // 2. CALCUL DU COMPTE DE RÉSULTAT (Memoized)
    // ====================================================================================
    useEffect(() => {
        if (isLoading) return;

        // --- I. PRODUITS (Basé sur PaiementsManagement) ---
        // Mapping: 'inscription' & 'mensualite' => Production Vendue. 'autre' => Autres Produits.
        let productionVendue = 0;
        let autresProduits = 0;

        revenuesData.forEach(p => {
            const montant = parseFloat(p.montantPaye) || 0;
            if (['inscription', 'mensualite'].includes(p.typePaiement)) {
                productionVendue += montant;
            } else {
                autresProduits += montant;
            }
        });
        const totalProduits = productionVendue + autresProduits;

        // --- II. CHARGES (Basé sur DepensesManagement) ---
        // Mapping des catégories textes vers postes comptables
        let achatsFournitures = 0;
        let servicesExterieurs = 0; // Loyer, Entretien, Transport, Pub, Factures
        let impotsTaxes = 0;
        let chargesPersonnel = 0;
        let fraisFinanciers = 0;
        let autresCharges = 0;

        expensesData.forEach(e => {
            const montant = parseFloat(e.amount) || 0;
            const cat = e.category || '';
            
            // Logique de mapping basée sur les catégories de DepensesManagement (source: 31)
            if (cat.includes('Fournitures')) achatsFournitures += montant;
            else if (cat.includes('Loyer') || cat.includes('Maintenance') || cat.includes('Transport') || cat.includes('Marketing') || cat.includes('Factures')) servicesExterieurs += montant;
            else if (cat.includes('Impôts')) impotsTaxes += montant;
            else if (cat.includes('Personnel') || e.isPayroll) chargesPersonnel += montant;
            else if (cat.includes('Financiers')) fraisFinanciers += montant;
            else autresCharges += montant;
        });

        // Dotations aux amortissements (Simulation basée sur le matériel)
        // Note: Le module matériel n'a pas encore de taux d'amortissement, on met 0 par défaut.
        const dotationsAmortissements = 0; 

        const totalChargesExploitation = achatsFournitures + servicesExterieurs + impotsTaxes + chargesPersonnel + autresCharges + dotationsAmortissements;

        // --- III. SOLDES ---
        const valeurAjoutee = totalProduits - (achatsFournitures + servicesExterieurs);
        const ebe = valeurAjoutee - chargesPersonnel - impotsTaxes; // Excédent Brut d'Exploitation
        const resultatExploitation = ebe - dotationsAmortissements;
        const resultatFinancier = 0 - fraisFinanciers;
        const resultatNet = resultatExploitation + resultatFinancier;

        setIncomeStatementData({
            productionVendue, autresProduits, totalProduits,
            achatsFournitures, servicesExterieurs, impotsTaxes, chargesPersonnel, autresCharges, fraisFinanciers, dotationsAmortissements,
            totalChargesExploitation,
            valeurAjoutee, ebe, resultatExploitation, resultatFinancier, resultatNet
        });

    }, [revenuesData, expensesData, isLoading]);


    // ====================================================================================
    // 3. CALCUL DU BILAN (Memoized)
    // ====================================================================================
    useEffect(() => {
        if (!incomeStatementData) return;

        // --- ACTIF ---
        
        // 1. Actif Immobilisé (Source: MaterielMobilierManagement)
        // ATTENTION: Le fichier MaterielMobilierManagement actuel n'a pas de champ 'price' ou 'value'.
        // Le code ci-dessous anticipe l'ajout d'un champ `unitPrice` ou `estimatedValue`.
        // Sinon il utilise 0.
        const totalMateriel = materialsData.reduce((sum, mat) => {
            const qty = parseInt(mat.quantity) || 0;
            const price = parseFloat(mat.unitPrice || mat.estimatedValue || 0);
            return sum + (qty * price);
        }, 0);

        // 2. Actif Circulant - Stocks (Source: ConsumablesManagement)
        const totalStocks = consumablesData.reduce((sum, item) => {
            const stock = parseInt(item.currentStock) || 0;
            const price = parseFloat(item.unitPrice) || 0;
            return sum + (stock * price);
        }, 0);

        // 3. Actif Circulant - Créances (Source: PaiementsManagement -> creances_clients)
        const totalCreances = creancesClientsData.reduce((sum, c) => sum + (parseFloat(c.montantDu) || 0), 0);

        // 4. Trésorerie Actif (Source: BankingManagement)
        // On prend uniquement les soldes positifs. Les découverts vont au passif.
        const soldeBancaireNet = bankAccountsData.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0);
        const tresorerieActif = soldeBancaireNet > 0 ? soldeBancaireNet : 0;
        
        const totalActif = totalMateriel + totalStocks + totalCreances + tresorerieActif;

        // --- PASSIF ---

        // 1. Capitaux Propres
        const capitalSocial = parseFloat(financialMetadata.capitalSocial) || 0;
        const reserves = parseFloat(financialMetadata.reserves) || 0;
        const resultatNet = incomeStatementData.resultatNet;
        const totalCapitaux = capitalSocial + reserves + resultatNet;

        // 2. Passif Non Courant (Source: DepensesManagement -> emprunts_lt)
        const totalEmpruntsLT = dettesLongTermeData.reduce((sum, d) => sum + (parseFloat(d.soldeEmprunt) || 0), 0);

        // 3. Passif Courant (Source: DepensesManagement -> dettes_fournisseurs)
        const totalDettesFournisseurs = dettesFournisseursData.reduce((sum, d) => sum + (parseFloat(d.montantDu) || 0), 0);
        
        // Trésorerie Passif (Découvert)
        const tresoreriePassif = soldeBancaireNet < 0 ? Math.abs(soldeBancaireNet) : 0;

        const totalPassif = totalCapitaux + totalEmpruntsLT + totalDettesFournisseurs + tresoreriePassif;
        const isBalanced = Math.abs(totalActif - totalPassif) < 500; // Tolérance mineure pour arrondis

        setBalanceSheetData({
            actif: {
                immo: totalMateriel,
                stocks: totalStocks,
                creances: totalCreances,
                tresorerie: tresorerieActif,
                total: totalActif
            },
            passif: {
                capital: capitalSocial,
                reserves: reserves,
                resultat: resultatNet,
                totalCapitaux,
                empruntsLT: totalEmpruntsLT,
                dettesF: totalDettesFournisseurs,
                tresorerie: tresoreriePassif,
                total: totalPassif
            },
            isBalanced
        });

    }, [incomeStatementData, materialsData, consumablesData, creancesClientsData, bankAccountsData, financialMetadata, dettesLongTermeData, dettesFournisseursData]);

    // --- Actions ---
    const handleUpdateCapital = async (e) => {
        e.preventDefault();
        if (!canManageCapitals) return;
        try {
            await setDoc(doc(db, 'schools', schoolId, 'financial_metadata', METADATA_DOC_ID), {
                capitalSocial: parseFloat(tempMetadata.capitalSocial),
                reserves: parseFloat(tempMetadata.reserves)
            }, { merge: true });
            setMessage("Capitaux mis à jour.");
        } catch (err) {
            setError("Erreur mise à jour capitaux.");
        }
    };

    // --- Rendu : Compte de Résultat ---
    const renderIncomeStatement = () => {
        if (!incomeStatementData) return <div className="p-4">Calcul en cours...</div>;
        const d = incomeStatementData;
        const Row = ({ label, val, bold = false, color = "text-gray-700" }) => (
            <div className={`flex justify-between py-2 border-b border-gray-100 ${bold ? 'font-bold bg-gray-50' : ''}`}>
                <span className={color}>{label}</span>
                <span className={color}>{formatAmount(val)}</span>
            </div>
        );

        return (
            <div className="max-w-3xl mx-auto bg-white p-6 shadow-lg border rounded-lg">
                <h3 className="text-center text-xl font-bold text-blue-900 mb-6">Compte de Résultat (Synthétique)</h3>
                
                <div className="mb-4">
                    <h4 className="font-bold text-green-700 mb-2 border-b border-green-200">I. PRODUITS D'EXPLOITATION</h4>
                    <Row label="Production Vendue (Scolarité)" val={d.productionVendue} />
                    <Row label="Autres Produits" val={d.autresProduits} />
                    <Row label="TOTAL PRODUITS" val={d.totalProduits} bold color="text-green-800" />
                </div>

                <div className="mb-4">
                    <h4 className="font-bold text-red-700 mb-2 border-b border-red-200">II. CHARGES D'EXPLOITATION</h4>
                    <Row label="Achats & Fournitures" val={d.achatsFournitures} />
                    <Row label="Services Extérieurs (Transports, Factures...)" val={d.servicesExterieurs} />
                    <Row label="Impôts et Taxes" val={d.impotsTaxes} />
                    <Row label="Charges de Personnel" val={d.chargesPersonnel} />
                    <Row label="Autres Charges" val={d.autresCharges} />
                    <Row label="TOTAL CHARGES" val={d.totalChargesExploitation} bold color="text-red-800" />
                </div>

                <div className="mb-6">
                    <Row label="RÉSULTAT D'EXPLOITATION (I - II)" val={d.resultatExploitation} bold color="text-blue-800" />
                </div>

                <div className="mb-4">
                    <h4 className="font-bold text-gray-700 mb-2 border-b">III. FINANCIAL & HAO</h4>
                    <Row label="Frais Financiers" val={d.fraisFinanciers} color="text-red-600" />
                </div>

                <div className={`p-4 rounded-lg text-center text-2xl font-bold text-white ${d.resultatNet >= 0 ? 'bg-green-600' : 'bg-red-600'}`}>
                    RÉSULTAT NET : {formatAmount(d.resultatNet)}
                </div>
            </div>
        );
    };

    // --- Rendu : Bilan ---
    const renderBalanceSheet = () => {
        if (!balanceSheetData) return <div className="p-4">Calcul en cours...</div>;
        const { actif, passif, isBalanced } = balanceSheetData;
        
        const Section = ({ title, children, color="blue" }) => (
            <div className={`flex-1 p-4 border border-${color}-200 rounded bg-${color}-50`}>
                <h4 className={`text-lg font-bold text-${color}-800 mb-3 border-b border-${color}-300 pb-2 uppercase`}>{title}</h4>
                {children}
            </div>
        );
        const Line = ({ label, val, sub = false }) => (
            <div className={`flex justify-between py-1 ${sub ? 'text-sm text-gray-600 pl-4' : 'font-medium text-gray-800'}`}>
                <span>{label}</span>
                <span>{formatAmount(val)}</span>
            </div>
        );

        return (
            <div className="space-y-6">
                {!isBalanced && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                        <p className="font-bold">Attention : Bilan déséquilibré</p>
                        <p>Différence : {formatAmount(Math.abs(actif.total - passif.total))}</p>
                        <p className="text-sm italic">Vérifiez que le capital social initial est bien renseigné.</p>
                    </div>
                )}

                {/* Gestion Capital */}
                {canManageCapitals && (
                     <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 mb-4 flex items-end gap-4">
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-indigo-700 uppercase">Capital Social</label>
                            <input type="number" value={tempMetadata.capitalSocial} onChange={e => setTempMetadata({...tempMetadata, capitalSocial: e.target.value})} className="w-full p-2 border rounded" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-indigo-700 uppercase">Réserves Reportées</label>
                            <input type="number" value={tempMetadata.reserves} onChange={e => setTempMetadata({...tempMetadata, reserves: e.target.value})} className="w-full p-2 border rounded" />
                        </div>
                        <button onClick={handleUpdateCapital} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 h-10">
                            Mettre à jour
                        </button>
                     </div>
                )}

                <div className="flex flex-col md:flex-row gap-6">
                    {/* ACTIF */}
                    <Section title="Actif (Emplois)" color="blue">
                        <h5 className="font-bold text-blue-900 mt-2">ACTIF IMMOBILISÉ</h5>
                        <Line label="Matériel & Mobilier" val={actif.immo} />
                        {actif.immo === 0 && <p className="text-xs text-red-500 italic text-right">*Aucune valeur définie dans l'inventaire</p>}
                        
                        <h5 className="font-bold text-blue-900 mt-4">ACTIF CIRCULANT</h5>
                        <Line label="Stocks (Consommables)" val={actif.stocks} />
                        <Line label="Créances Clients (Scolarités dues)" val={actif.creances} />
                        
                        <h5 className="font-bold text-blue-900 mt-4">TRÉSORERIE ACTIF</h5>
                        <Line label="Disponibilités (Banques/Caisses)" val={actif.tresorerie} />

                        <div className="mt-6 pt-4 border-t-2 border-blue-400 flex justify-between text-xl font-bold text-blue-900">
                            <span>TOTAL ACTIF</span>
                            <span>{formatAmount(actif.total)}</span>
                        </div>
                    </Section>

                    {/* PASSIF */}
                    <Section title="Passif (Ressources)" color="green">
                        <h5 className="font-bold text-green-900 mt-2">CAPITAUX PROPRES</h5>
                        <Line label="Capital Social" val={passif.capital} />
                        <Line label="Réserves" val={passif.reserves} />
                        <Line label="Résultat Net" val={passif.resultat} />
                        <div className="pl-4 border-l-2 border-green-200 my-1">
                            <Line label="Total Capitaux Propres" val={passif.totalCapitaux} sub />
                        </div>

                        <h5 className="font-bold text-green-900 mt-4">DETTES FINANCIÈRES (LT)</h5>
                        <Line label="Emprunts" val={passif.empruntsLT} />

                        <h5 className="font-bold text-green-900 mt-4">PASSIF CIRCULANT (CT)</h5>
                        <Line label="Dettes Fournisseurs" val={passif.dettesF} />
                        <Line label="Découverts Bancaires" val={passif.tresorerie} />

                        <div className="mt-6 pt-4 border-t-2 border-green-400 flex justify-between text-xl font-bold text-green-900">
                            <span>TOTAL PASSIF</span>
                            <span>{formatAmount(passif.total)}</span>
                        </div>
                    </Section>
                </div>
            </div>
        );
    };

    // --- Rendu Principal ---
    return (
        <div className="p-6 bg-white shadow rounded-lg min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">États Financiers Automatisés (SYSCOHADA)</h1>
                {isLoading && <span className="flex items-center text-blue-600"><RefreshCw className="animate-spin mr-2"/> Actualisation des données...</span>}
            </div>

            {/* Navigation */}
            <div className="flex space-x-4 border-b mb-6">
                <button 
                    onClick={() => setActiveTab('balanceSheet')}
                    className={`pb-2 px-4 border-b-2 font-medium flex items-center ${activeTab === 'balanceSheet' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <ClipboardList className="w-5 h-5 mr-2" /> Bilan
                </button>
                <button 
                    onClick={() => setActiveTab('incomeStatement')}
                    className={`pb-2 px-4 border-b-2 font-medium flex items-center ${activeTab === 'incomeStatement' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <BarChart2 className="w-5 h-5 mr-2" /> Compte de Résultat
                </button>
            </div>

            {/* Contenu */}
            <div className="bg-gray-50 p-4 rounded-lg min-h-[400px]">
                {!canAccessFinancials ? (
                    <div className="text-red-500 text-center mt-10">Accès non autorisé.</div>
                ) : (
                    <>
                        {activeTab === 'balanceSheet' && renderBalanceSheet()}
                        {activeTab === 'incomeStatement' && renderIncomeStatement()}
                    </>
                )}
            </div>

            {/* Note informative */}
            <div className="mt-6 p-4 bg-blue-50 text-blue-800 rounded text-sm border border-blue-200 flex items-start">
                <BookOpen className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                    <strong>Intégration Système :</strong> Ce module compile automatiquement les données de :
                    <ul className="list-disc ml-5 mt-1">
                        <li>Recettes (Scolarité) depuis <em>Gestion des Paiements</em>.</li>
                        <li>Charges, Dettes Fournisseurs & Emprunts depuis <em>Gestion des Dépenses</em>.</li>
                        <li>Valeur des Stocks depuis <em>Gestion des Consommables</em>.</li>
                        <li>Trésorerie réelle depuis <em>Gestion Bancaire</em>.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default FinancialStatementsManagement;